/**
 * PromptsPage
 *
 * Main page for prompt management. Switches between three modes:
 * - Grid: shows all prompt key cards (grouped by taxonomy when "All" selected)
 * - Detail: shows a specific prompt's template + version sidebar
 * - Editor: full-screen split-pane template editor
 *
 * A taxonomy filter dropdown lets users view prompts for a specific taxonomy
 * or all taxonomies at once.
 */

import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ErrorBoundary } from '../../../ui/error-boundary';
import { Button } from '../../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { usePromptKeys } from '../hooks/usePrompts';
import { useTaxonomyKeys } from '../hooks/useTaxonomy';
import GOLabRegistryNav from '../shared/GOLabRegistryNav';
import PromptKeyCard from './components/PromptKeyCard';
import PromptDetailView from './components/PromptDetailView';
import PromptEditorView from './components/PromptEditorView';
import CreatePromptModal from './components/CreatePromptModal';
import TimelineDrawer from '../taxonomy/components/TimelineDrawer';

const PromptsPage: React.FC = () => {
  const { promptKey } = useParams<{ promptKey?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [createNewOpen, setCreateNewOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);

  // Editor mode is URL-driven via `?edit=<versionId>` so browser refresh
  // preserves the editor instead of dumping the user back to detail view.
  const editorVersionId = searchParams.get('edit');

  const setEditorVersionId = (versionId: string | null) => {
    if (versionId) {
      searchParams.set('edit', versionId);
    } else {
      searchParams.delete('edit');
    }
    setSearchParams(searchParams);
  };

  // Taxonomy filter from URL search params (null = all)
  const selectedTaxonomy = searchParams.get('taxonomyKey') || null;

  const { data: taxonomyKeys } = useTaxonomyKeys();
  const { data: keys, isLoading, isError, refetch } = usePromptKeys(selectedTaxonomy);

  // Group prompts by taxonomy for the "All" view
  const groupedPrompts = useMemo(() => {
    if (!keys || selectedTaxonomy) return null;
    const groups: Record<string, typeof keys> = {};
    for (const k of keys) {
      const tk = k.taxonomyKey || 'unknown';
      if (!groups[tk]) groups[tk] = [];
      groups[tk].push(k);
    }
    return groups;
  }, [keys, selectedTaxonomy]);

  const handleFilterChange = (value: string) => {
    if (value === '__all__') {
      searchParams.delete('taxonomyKey');
    } else {
      searchParams.set('taxonomyKey', value);
    }
    setSearchParams(searchParams);
  };

  const getTaxonomyDisplayName = (tk: string) =>
    taxonomyKeys?.find((t) => t.taxonomyKey === tk)?.displayName || tk;

  const pageHeader = (
    <div className="space-y-4">
      <div className="space-y-2">
        <h1 className="font-jakarta text-3xl font-extrabold tracking-[-0.04em] text-foreground md:text-4xl">
          Intelligence
        </h1>
        <p className="max-w-3xl text-[13px] leading-[1.7] text-muted-foreground">
          Manage the taxonomy and prompt registries that power classification and evaluation.
        </p>
      </div>
      <GOLabRegistryNav />
    </div>
  );

  // Editor mode
  if (editorVersionId) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-[var(--go-spring)] px-4 py-4 md:px-6 md:py-6 xl:px-8 xl:py-6">
        <div className="flex min-h-0 flex-1 flex-col gap-6">
          {pageHeader}
          <div className="min-h-0 flex-1">
            <PromptEditorView
              versionId={editorVersionId}
              taxonomyDisplayName={selectedTaxonomy ? getTaxonomyDisplayName(selectedTaxonomy) : undefined}
              onBack={() => setEditorVersionId(null)}
            />
          </div>
        </div>
      </div>
    );
  }

  // Detail mode
  if (promptKey) {
    const summary = keys?.find((k) => k.promptKey === promptKey);
    const detailTaxonomyKey = summary?.taxonomyKey || selectedTaxonomy || taxonomyKeys?.[0]?.taxonomyKey || '';
    return (
      <div className="flex h-full min-h-0 flex-col bg-[var(--go-spring)] px-4 py-4 md:px-6 md:py-6 xl:px-8 xl:py-6">
        <div className="flex min-h-0 flex-1 flex-col gap-6">
          {pageHeader}
          <div className="min-h-0 flex-1">
            <PromptDetailView
              promptKey={promptKey}
              taxonomyKey={detailTaxonomyKey}
              taxonomyDisplayName={getTaxonomyDisplayName(detailTaxonomyKey)}
              displayName={summary?.displayName || promptKey}
              onEditDraft={(versionId) => setEditorVersionId(versionId)}
            />
          </div>
        </div>
      </div>
    );
  }

  // Grid mode
  if (isLoading) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-auto bg-[var(--go-spring)] px-4 py-4 md:px-6 md:py-6 xl:px-8 xl:py-6">
        <div className="space-y-6">
          {pageHeader}
          <div className="h-8 w-56 animate-pulse rounded bg-muted" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="h-[120px] animate-pulse rounded-lg border border-border/70 bg-card" />
          ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-auto bg-[var(--go-spring)] px-4 py-4 md:px-6 md:py-6 xl:px-8 xl:py-6">
        <div className="space-y-6">
          {pageHeader}
          <div className="flex h-[50vh] flex-col items-center justify-center text-center">
            <p className="mb-4 text-muted-foreground">Failed to load prompts.</p>
            <Button variant="link" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-auto bg-[var(--go-spring)] px-4 py-4 md:px-6 md:py-6 xl:px-8 xl:py-6">
      <div className="space-y-6">
        {pageHeader}

        <section className="space-y-5 rounded-2xl border border-border/70 bg-card p-6 shadow-[var(--shadow-card)]">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="font-jakarta text-2xl font-extrabold tracking-[-0.03em] text-foreground">
                Prompt Registry
              </h2>
              <p className="mt-1 text-[13px] leading-[1.7] text-muted-foreground">
                Manage classification prompt templates and version history.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="toolbar"
                size="sm"
                onClick={() => setCreateNewOpen(true)}
              >
                Add New Prompt
              </Button>
              <Select value={selectedTaxonomy || '__all__'} onValueChange={handleFilterChange}>
                <SelectTrigger size="sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Taxonomies</SelectItem>
                  {taxonomyKeys?.map((t) => (
                    <SelectItem key={t.taxonomyKey} value={t.taxonomyKey}>
                      {t.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="secondary"
                size="sm"
                className="h-8 rounded-md border-input bg-transparent px-3 text-sm font-normal text-foreground shadow-xs hover:bg-accent"
                onClick={() => setTimelineOpen(true)}
              >
                View Timeline
              </Button>
            </div>
          </div>

          {/* Grouped view (All taxonomies) */}
          {groupedPrompts && Object.entries(groupedPrompts).map(([tk, prompts]) => (
            <div key={tk}>
              <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {getTaxonomyDisplayName(tk)}
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {prompts.map((summary) => (
                  <PromptKeyCard
                    key={`${summary.taxonomyKey}-${summary.promptKey}`}
                    summary={summary}
                    onClick={() => navigate(`/v4/go-lab/intelligence/prompts/${summary.promptKey}?taxonomyKey=${summary.taxonomyKey}`)}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Flat view (specific taxonomy selected) */}
          {!groupedPrompts && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {keys?.map((summary) => (
                <PromptKeyCard
                  key={summary.promptKey}
                  summary={summary}
                  onClick={() => navigate(`/v4/go-lab/intelligence/prompts/${summary.promptKey}?taxonomyKey=${summary.taxonomyKey}`)}
                />
              ))}
            </div>
          )}

          {(!keys || keys.length === 0) && (
            <p className="py-8 text-center text-muted-foreground">
              No prompts found. Run the seed script to create initial prompts.
            </p>
          )}
        </section>

        <CreatePromptModal
          taxonomyKey={selectedTaxonomy || ''}
          open={createNewOpen}
          onOpenChange={setCreateNewOpen}
        />
        {(selectedTaxonomy || taxonomyKeys?.[0]?.taxonomyKey) && (
          <TimelineDrawer
            taxonomyKey={selectedTaxonomy || taxonomyKeys![0].taxonomyKey}
            open={timelineOpen}
            onOpenChange={setTimelineOpen}
          />
        )}
      </div>
    </div>
  );
};

const PromptsPageWithBoundary: React.FC = () => (
  <ErrorBoundary>
    <PromptsPage />
  </ErrorBoundary>
);

export default PromptsPageWithBoundary;
